
import { createContext, useState, useEffect, useContext, ReactNode, useRef } from 'react';
import { translateText, translateTextBatch, getAvailableLanguages } from '@/lib/translation';
import { toast } from 'sonner';
import { getCombinedDictionary, normalizeText, fetchAndCacheCloudDictionary } from '@/i18n/dictionary';

// Define types
type Language = {
  code: string;
  name: string;
};

interface TranslationContextType {
  currentLanguage: string;
  setLanguage: (lang: string) => void;
  translate: (text: string, forceTranslate?: boolean) => Promise<string>;
  isTranslating: boolean;
  availableLanguages: Language[];
  originalLanguage: string;
  staticOnlyMode: boolean;
  setStaticOnlyMode: (v: boolean) => void;
}

// Type for the translation cache
type TranslationCache = Record<string, Record<string, string>>;

// Translation batch queue types
type TranslationRequest = {
  text: string;
  priority: number;
  resolve: (value: string) => void;
  reject: (error: Error) => void;
  forceTranslate: boolean;
};

// Create context with default values
const TranslationContext = createContext<TranslationContextType>({
  currentLanguage: 'sv',
  setLanguage: () => {},
  translate: async () => '',
  isTranslating: false,
  availableLanguages: [],
  originalLanguage: 'sv',
  staticOnlyMode: false,
  setStaticOnlyMode: () => {},
});

const PRIORITY = {
  FORCE: -1,  // Highest priority for forced translations
  HIGH: 0,    // High priority for key UI elements
  MEDIUM: 1,  // Medium priority for most content
  LOW: 2      // Low priority for less important content
};

// Translation provider component
export const TranslationProvider = ({ children }: { children: ReactNode }) => {
  const [currentLanguage, setCurrentLanguage] = useState<string>('sv');
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationCache, setTranslationCache] = useState<TranslationCache>({});
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const originalLanguage = 'sv'; // Default original language is Swedish
  const [translationQueue, setTranslationQueue] = useState<TranslationRequest[]>([]);
  const [isProcessingQueue, setIsProcessingQueue] = useState(false);
  const [consecutiveFailures, setConsecutiveFailures] = useState(0);
  const previousLanguage = useRef(currentLanguage);
  const [staticOnlyMode, setStaticOnlyMode] = useState<boolean>(false);
  
  // Batch processing state
  const [batchQueue, setBatchQueue] = useState<Map<string, TranslationRequest[]>>(new Map());
  const [isBatchProcessing, setIsBatchProcessing] = useState(false);
  const batchTimeout = useRef<NodeJS.Timeout>();
  const BATCH_SIZE = 15; // Process up to 15 texts per batch
  const BATCH_DELAY = 300; // Wait 300ms before processing batch

  // Initialize from localStorage on component mount
  useEffect(() => {
    try {
      if (typeof window === 'undefined') return;
      
      // Load preferred language
      const savedLanguage = localStorage.getItem('preferred_language');
      if (savedLanguage) {
        setCurrentLanguage(JSON.parse(savedLanguage));
      }
      
      // Load translation cache
      const savedCache = localStorage.getItem('translation_cache');
      if (savedCache) {
        setTranslationCache(JSON.parse(savedCache));
      }
      
      // Load static only mode
      const savedStaticMode = localStorage.getItem('static_only_mode');
      if (savedStaticMode) {
        setStaticOnlyMode(JSON.parse(savedStaticMode));
      }
      
      // Load available languages
      const languages = getAvailableLanguages();
      setAvailableLanguages(languages);
    } catch (error) {
      console.error('Failed to initialize from localStorage:', error);
    }
  }, []);

  // Save currentLanguage to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('preferred_language', JSON.stringify(currentLanguage));
    } catch (error) {
      console.error('Failed to save preferred language:', error);
    }
  }, [currentLanguage]);

  // Save translationCache to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('translation_cache', JSON.stringify(translationCache));
    } catch (error) {
      console.error('Failed to save translation cache:', error);
    }
  }, [translationCache]);

  // Save staticOnlyMode to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('static_only_mode', JSON.stringify(staticOnlyMode));
    } catch (error) {
      console.error('Failed to save static only mode:', error);
    }
  }, [staticOnlyMode]);

  // Clear translation cache when language changes and warm with dictionaries (OPTIMIZED: No longer clears cache)
  useEffect(() => {
    (async () => {
      const changed = currentLanguage !== previousLanguage.current;
      if (changed) {
        // Clear the translation queue when language changes
        setTranslationQueue([]);
        setBatchQueue(new Map()); // Clear batch queue as well
        setConsecutiveFailures(0);
        previousLanguage.current = currentLanguage;
        
        // OPTIMIZATION: No longer clear cache, but mark elements for re-translation
        // This allows cached translations to remain available while forcing visible content to update
        if (document) {
          const translatedElements = document.querySelectorAll('[data-translated="true"]');
          translatedElements.forEach(element => {
            element.removeAttribute('data-translated');
          });
          // Trigger a global re-translation for visible content only
          setTimeout(() => {
            try {
              const event = new CustomEvent('force-translate');
              window.dispatchEvent(event);
            } catch (e) {
              console.warn('Failed to dispatch force-translate event', e);
            }
          }, 50);
        }
      }

      // Warm cache with combined dictionary for the selected language
      if (currentLanguage !== originalLanguage) {
        try {
          await fetchAndCacheCloudDictionary(currentLanguage);
        } catch (e) {
          console.warn('Could not refresh cloud dictionary', e);
        }
        const dict = getCombinedDictionary(currentLanguage);
        const updated = { ...translationCache } as TranslationCache;
        if (!updated[currentLanguage]) updated[currentLanguage] = {};
        for (const [svText, tr] of Object.entries(dict)) {
          // Keep original (unnormalized) keys in cache for exact matches at render time
          updated[currentLanguage][svText] = tr;
        }
        setTranslationCache(updated);
      }
    })();
  }, [currentLanguage]);

  // Process batch queue - New optimized batch processing
  useEffect(() => {
    const processBatchQueue = async () => {
      if (batchQueue.size === 0 || isBatchProcessing) return;
      
      setIsBatchProcessing(true);
      
      try {
        // Process each language's batch
        for (const [targetLang, requests] of batchQueue.entries()) {
          if (requests.length === 0) continue;
          
          // Take up to BATCH_SIZE requests
          const batch = requests.slice(0, BATCH_SIZE);
          const textsToTranslate = batch.map(req => req.text);
          
          console.log(`Processing batch of ${batch.length} texts for language ${targetLang}`);
          
          try {
            // Use batch translation API
            const batchResults = await translateTextBatch(textsToTranslate, targetLang);
            
            // Update cache with all results
            const updatedCache = { ...translationCache };
            if (!updatedCache[targetLang]) {
              updatedCache[targetLang] = {};
            }
            
            // Resolve all promises and update cache
            batch.forEach(request => {
              const translatedText = batchResults[request.text] || request.text;
              updatedCache[targetLang][request.text] = translatedText;
              request.resolve(translatedText);
            });
            
            setTranslationCache(updatedCache);
            setConsecutiveFailures(0);
            
          } catch (error) {
            console.error('Batch translation failed, falling back to individual translations:', error);
            
            // Fallback to individual translations for this batch
            for (const request of batch) {
              try {
                const translatedText = await translateText(request.text, targetLang);
                const updatedCache = { ...translationCache };
                if (!updatedCache[targetLang]) {
                  updatedCache[targetLang] = {};
                }
                updatedCache[targetLang][request.text] = translatedText;
                setTranslationCache(updatedCache);
                request.resolve(translatedText);
              } catch (individualError) {
                console.error('Individual translation also failed:', individualError);
                request.reject(individualError instanceof Error ? individualError : new Error('Translation failed'));
              }
            }
            
            setConsecutiveFailures(prev => prev + 1);
          }
          
          // Remove processed requests from batch queue
          setBatchQueue(prev => {
            const newMap = new Map(prev);
            const remaining = requests.slice(BATCH_SIZE);
            if (remaining.length > 0) {
              newMap.set(targetLang, remaining);
            } else {
              newMap.delete(targetLang);
            }
            return newMap;
          });
        }
        
      } finally {
        setIsBatchProcessing(false);
      }
    };
    
    if (batchQueue.size > 0) {
      processBatchQueue();
    }
  }, [batchQueue, isBatchProcessing, currentLanguage, translationCache]);

  // Function to translate text with batch optimization
  const translate = async (text: string, forceTranslate = false): Promise<string> => {
    // Don't translate if we're already using the original language
    if (currentLanguage === originalLanguage) {
      return text;
    }
    
    // Don't translate empty text
    if (!text || text.trim() === '') {
      return text;
    }

    // 1) Try static dictionary first (fast path)
    const dict = getCombinedDictionary(currentLanguage);
    const dictKey = normalizeText(text);
    const dictHit = dict[dictKey];
    if (dictHit) {
      // Update cache for exact text and return immediately
      const updated = { ...translationCache } as TranslationCache;
      if (!updated[currentLanguage]) updated[currentLanguage] = {};
      updated[currentLanguage][text] = dictHit;
      setTranslationCache(updated);
      return dictHit;
    }

    // 2) Try cache next for immediate response if not forcing translation
    if (!forceTranslate && 
        translationCache[currentLanguage] && 
        translationCache[currentLanguage][text]) {
      return translationCache[currentLanguage][text];
    }

    // 3) If static-only mode is enabled, do not call API — return original text (unless forced)
    if (staticOnlyMode && !forceTranslate) {
      return text;
    }

    try {
      setIsTranslating(true);
      
      // 4) Add to batch queue for optimized processing
      return new Promise<string>((resolve, reject) => {
        const priority = forceTranslate ? PRIORITY.FORCE : PRIORITY.MEDIUM;
        const request: TranslationRequest = {
          text,
          priority,
          resolve,
          reject,
          forceTranslate
        };

        // Add to batch queue grouped by target language
        setBatchQueue(prev => {
          const newMap = new Map(prev);
          const existing = newMap.get(currentLanguage) || [];
          
          // Check if this text is already queued to avoid duplicates
          const alreadyQueued = existing.some(req => req.text === text);
          if (!alreadyQueued) {
            newMap.set(currentLanguage, [...existing, request]);
          } else {
            // If already queued, resolve with existing promise or queue this one too
            newMap.set(currentLanguage, [...existing, request]);
          }
          
          return newMap;
        });

        // Clear existing timeout and set new one for batch processing
        if (batchTimeout.current) {
          clearTimeout(batchTimeout.current);
        }
        
        batchTimeout.current = setTimeout(() => {
          // Trigger batch processing by updating a dummy state
          setBatchQueue(prev => new Map(prev));
        }, BATCH_DELAY);
      });
      
    } catch (error) {
      console.error('Translation error:', error);
      return text; // Return original text on error
    } finally {
      // Only set to false when no more batches are processing
      setTimeout(() => {
        if (batchQueue.size === 0) {
          setIsTranslating(false);
        }
      }, 1000);
    }
  };

  // Change language handler
  const setLanguage = (lang: string) => {
    if (lang !== currentLanguage) {
      console.log(`Changing language from ${currentLanguage} to ${lang}`);
      
      // Clear queue when language changes to avoid stale translations
      setTranslationQueue([]);
      setBatchQueue(new Map()); // Clear batch queue as well
      setConsecutiveFailures(0);
      
      // Update language preference
      setCurrentLanguage(lang);
    }
  };

  return (
    <TranslationContext.Provider
      value={{
        currentLanguage,
        setLanguage,
        translate,
        isTranslating,
        availableLanguages,
        originalLanguage,
        staticOnlyMode,
        setStaticOnlyMode,
      }}
    >
      {children}
    </TranslationContext.Provider>
  );
};

// Custom hook for using the translation context
export const useTranslation = () => useContext(TranslationContext);
